'use client';
/**
 * Équipements × postes de travail — lots N7-3 et N7-4.
 *
 * ── Pourquoi un fichier de composants et pas un écran de plus ──────────────
 *
 * Le rattachement ne vaut que par les deux questions qu'il permet de poser, et
 * ces deux questions ne se posent pas au même endroit :
 *
 *  · sur la fiche d'un POSTE — « une personne affectée ici, sur quoi doit-elle
 *    être habilitée ? » ;
 *  · sur la fiche d'un ÉQUIPEMENT — « qui peut faire tourner cet automate ? ».
 *
 * Un écran isolé « rattachements » n'aurait répondu ni à l'une ni à l'autre :
 * on ne l'ouvre que si l'on sait déjà qu'il existe. Les trois composants d'ici
 * vont donc SE POSER dans les écrans qui existent — composition des postes,
 * fiche d'équipement, formulaire d'ajout — et ils lisent tous la même route,
 * pour qu'aucun ne puisse dire autre chose que l'autre.
 *
 * ⚠️ Adresse réelle : `/api/ged/ged/…` — DOUBLE `ged`. Le proxy est monté sur
 * `/api/ged/` et le moteur porte aussi `ged`. Ce piège a rendu toute la famille
 * N7 injoignable une première fois.
 */
import React from 'react';
import { fetchEcriture } from '@/lib/ecrire';
import { I, card } from '@/components/ui';

import { Bouton } from '@/shared/Bouton';
import { Tableau } from '@/shared/Tableau';
import { useGed } from '@/lib/store';
/* ------------------------------------------------------------------------ */
/* Ce que le moteur rend                                                     */
/* ------------------------------------------------------------------------ */

export type EquipementDuPoste = {
  code: string; nom: string;
  origine: 'pose' | 'habilitation';
  habilitation?: string;
  par?: string | null; le?: string | null; statut?: string | null;
  accessoires: { code: string; nom: string; statut?: string | null }[];
};
export type VuePoste = {
  code: string; libelle: string;
  equipements: EquipementDuPoste[];
  introuvables: string[]; accessoiresPoses: string[];
  nbPoses: number; nbParHabilitation: number; nbAccessoires: number;
};
export type Chiffrage = {
  equipements: number; mesure: number; accessoires: number;
  rattaches: number; rattachesPose: number; rattachesHabilitation: number;
  sansPoste: number; postes: number; postesAvecEquipement: number;
  postesSansEquipement: string[]; accessoiresSansAutomate: number;
  /** parmi les `sansPoste`, ceux qu'une décision signée a ÉCARTÉS de la reprise */
  sansPosteEcartes: number;
  /** ce qui RESTE à traiter : `sansPoste − sansPosteEcartes`. Deux chiffres et
   *  non un — une fiche écartée reste une fiche qu'aucun poste n'utilise, et
   *  l'auditeur a le droit de la compter ; mais la séance ne doit plus la
   *  redemander. */
  sansPosteATraiter: number;
};
/** Une fiche ÉCARTÉE de la reprise — la décision voyage avec elle. */
export type LigneEcartee = {
  equipement: string; nom: string;
  discipline?: string | null; localisation?: string | null;
  ecartement: { motif: string; par: string; le: string | null };
};
export type Proposition = {
  code: string; libelle: string; motif: string;
  confiance: 'sur' | 'probable'; signaux: string[];
};
export type ResultatProposition = {
  propositions: Proposition[];
  silence: string | null;
  redirection: { automate: string | null; message: string } | null;
  ecartes: { code: string; libelle: string; pourquoi: string }[];
};
export type VueEquipement = {
  code: string;
  postes: { code: string; libelle: string; origine: 'pose' | 'habilitation'; habilitation?: string }[];
  sertAutomate?: string | null;
  posteHerites?: { code: string; libelle: string; via: string }[];
  note?: string;
};

const encadre = (fond: string, bord: string): React.CSSProperties => ({
  ...card, padding: 14, background: fond, border: `1px solid ${bord}`,
});

/** La pastille qui distingue les deux liens. Elle n'est pas décorative : c'est
 *  elle qui empêche de croire qu'un équipement « posé » exige une habilitation
 *  qui n'existe pas, et qu'un équipement « exigé » se détache d'un clic. */
export function OrigineLien({ e }: { e: { origine: 'pose' | 'habilitation'; habilitation?: string } }) {
  const exige = e.origine === 'habilitation';
  return (
    <span title={exige
      ? `Exigé par l’habilitation « ${e.habilitation} » : on ne peut pas tenir ce poste sans elle. Pour le retirer, retirez l’habilitation des exigences du poste.`
      : 'Posé à la main avant le 28/10/2026 : à convertir en habilitation ou à retirer à la reprise.'}
      style={{
        display: 'inline-block', fontSize: 12.5, fontWeight: 700, padding: '2px 7px',
        borderRadius: 'var(--radius-pill)', whiteSpace: 'nowrap',
        background: exige ? 'var(--blue-50)' : 'var(--gray-50)',
        color: exige ? 'var(--blue-700)' : 'var(--text-muted)',
        border: `1px solid ${exige ? 'var(--blue-300)' : 'var(--border-default)'}`,
      }}>
      {exige ? `exigé · ${e.habilitation}` : 'ancien rattachement à la main'}
    </span>
  );
}

/* ------------------------------------------------------------------------ */
/* 1 · Le panneau du POSTE — N7-3                                            */
/* ------------------------------------------------------------------------ */

/**
 * « Quels équipements utilise-t-on à ce poste ? », et le geste de le corriger.
 *
 * Le tableau distingue toujours les deux liens. Seuls les équipements POSÉS
 * portent un bouton « Retirer » : ceux qu'une habilitation exige ne se
 * détachent pas d'ici, et l'écran dit où aller pour cela plutôt que d'offrir un
 * bouton qui échouerait.
 */
export function PanneauEquipementsPoste({ posteCode, posteLibelle }: { posteCode: string; posteLibelle: string }) {
  const [vue, setVue] = React.useState<VuePoste | null>(null);
  const [erreur, setErreur] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [brouillon, setBrouillon] = React.useState<string[] | null>(null);
  const [enCours, setEnCours] = React.useState(false);
  // Un COMPTEUR de rechargement, jamais un setState au corps d'un effet :
  // `react-hooks/set-state-in-effect` est une erreur de lint dans ce dépôt.
  const [tour, setTour] = React.useState(0);

  React.useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/rh/postes/equipements', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!vivant) return;
        const v = (j?.postes || []).find((p: VuePoste) => p.code === posteCode) || null;
        if (j) { setVue(v); setErreur(''); } else setErreur('Les équipements des postes n’ont pas pu être lus.');
      })
      .catch(() => { if (vivant) setErreur('Les équipements des postes n’ont pas pu être lus.'); });
    return () => { vivant = false; };
  }, [posteCode, tour]);

  if (erreur) {
    return <div style={encadre('var(--red-50)', 'var(--red-300)')}><strong style={{ fontSize: 12.5 }}>{erreur}</strong></div>;
  }
  if (!vue) return <div style={{ ...card, padding: 18 }}><p style={{ fontSize: 12.5, margin: 0 }}>Lecture…</p></div>;

  const poses = vue.equipements.filter((e) => e.origine === 'pose').map((e) => e.code);
  const choix = brouillon ?? poses;
  const modifie = brouillon !== null;

  const enregistrer = async () => {
    setEnCours(true);
    const r = await fetchEcriture(`/api/ged/ged/rh/postes/${encodeURIComponent(posteCode)}/equipements`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      /* `base` (14/10/2026) : les appareils posés LUS. Le moteur n'applique
         que les ajouts et retraits de cet écran à la liste fraîche. */
      body: JSON.stringify({ equipements: choix.map((code) => ({ code })), base: poses }),
    });
    const d = await r.json().catch(() => null);
    setEnCours(false);
    if (!r.ok) { setErreur(d?.error?.message || 'Rattachement refusé.'); return; }
    setErreur('');
    setMessage('Ancien(s) rattachement(s) retiré(s).');
    setBrouillon(null);
    setTour((t) => t + 1);
  };

  return (
    <div style={{ ...card, padding: 18, marginTop: 14 }}>
      <h2 style={{ fontSize: 14, margin: '0 0 4px' }}>
        Équipements de ce poste — {vue.equipements.length}
        {modifie && <span style={{ color: 'var(--amber-600)', fontWeight: 600, fontSize: 12.5 }}> · non enregistré</span>}
      </h2>
      {/* LE LIEN SE DÉDUIT (Ines, 28/10/2026) : un appareil sert à ce poste dès
          que le poste exige une habilitation qui le couvre. On ne l'ajoute plus
          ici ; on change les exigences du poste, ou les habilitations de
          l'appareil depuis sa fiche. */}
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 12px', lineHeight: 1.5 }}>
        Les appareils couverts par les habilitations qu’exige « {posteLibelle} » — c’est la réponse à
        « une personne affectée ici, sur quoi doit-elle être habilitée ? ». Pour changer cette liste,
        modifiez les habilitations exigées par le poste, ou celles d’un appareil depuis sa fiche.
        {vue.nbPoses > 0 && <> <strong>{vue.nbPoses}</strong> ancien(s) rattachement(s) à la main restent à reprendre.</>}
      </p>

      {!!message && (
        <div style={{ ...encadre('var(--green-50)', 'var(--green-700)'), marginBottom: 12 }}>
          <strong style={{ fontSize: 12.5, color: 'var(--green-700)' }}>{message}</strong>
        </div>
      )}

      {/* L10 (11/10/2026) : sous-tableau SIMPLE sur le socle — « Retirer » en colonne
          d'actions, les accessoires repliés sous la désignation. */}
      <Tableau
        id="poste-equipements"
        ajusterLargeur
        niveau="simple"
        lignes={vue.equipements}
        cleLigne={(e) => e.code}
        vide={{ titre: 'Aucun équipement à ce poste', indice: 'Ni exigé par une habilitation, ni posé à la main. Ce n’est pas forcément un manque — un poste d’accueil n’utilise aucun automate.' }}
        colonnes={[
          /* Code et nom dans DEUX colonnes : « EQ03 — Erba Lyte Pro » d'un bloc ne
             s'alignait pas d'une ligne à l'autre. */
          { cle: 'code', libelle: 'Code', valeur: (e) => e.code, style: { width: 72, fontFamily: 'var(--font-mono)', fontWeight: 700, verticalAlign: 'top' }, rendu: (e) => e.code },
          {
            cle: 'nom', libelle: 'Équipement', valeur: (e) => e.nom, style: { lineHeight: 1.45 },
            rendu: (e) => (
              <>
                {e.nom}
                {e.statut === 'hors_service' && <span style={{ color: 'var(--red-600)', fontSize: 12.5, fontWeight: 700 }}> · hors service</span>}
                {!!e.accessoires.length && (
                  <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3, paddingInlineStart: 12, borderInlineStart: '2px solid var(--border-default)' }}>
                    {/* Les accessoires SUIVENT : ils ne se rattachent pas, mais une panne de clavier immobilise bien le poste. */}
                    {e.accessoires.map((a) => `${a.code} ${a.nom}`).join(' · ')}
                    <br />
                    <em>accessoire(s) servant cet appareil — ils suivent, ils ne se rattachent pas séparément</em>
                  </div>
                )}
              </>
            ),
          },
          { cle: 'origine', libelle: 'Origine', type: 'liste', valeur: (e) => e.origine, libelleValeur: (v) => (v === 'pose' ? 'posé à la main' : 'exigé par une habilitation'), style: { width: 210 }, rendu: (e) => <OrigineLien e={e} /> },
        ]}
        actions={(e) => (e.origine === 'pose' ? (
          <Bouton taille="sm" onClick={() => { setMessage(''); setBrouillon(choix.filter((c) => c !== e.code)); }}
            title="Ancien rattachement à la main : le retirer">
            <I n="x" s={12} /> Retirer
          </Bouton>
        ) : (
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }} title="Déduit de l’habilitation exigée par le poste.">
            via l’habilitation
          </span>
        ))}
      />

      {!!vue.introuvables.length && (
        <p style={{ fontSize: 12.5, color: 'var(--amber-600)', margin: '10px 0 0', lineHeight: 1.5 }}>
          {vue.introuvables.length} code(s) rattaché(s) ne correspondent à aucune fiche du parc
          ({vue.introuvables.join(' · ')}). Rien n’a été effacé : un rattachement supprimé en silence ne
          se verrait pas passer.
        </p>
      )}
      {!!vue.accessoiresPoses.length && (
        <p style={{ fontSize: 12.5, color: 'var(--amber-600)', margin: '10px 0 0', lineHeight: 1.5 }}>
          {vue.accessoiresPoses.join(' · ')} : ce sont des ÉQUIPEMENTS ACCESSOIRES. Ils ne se rattachent pas
          à un poste — ils suivent l’automate qu’ils servent. À corriger depuis « Accessoires &amp; qualification ».
        </p>
      )}

      {modifie && (
        <div style={{ display: 'flex', gap: 8, marginTop: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          <Bouton taille="sm" onClick={() => { setBrouillon(null); setMessage(''); }}>Annuler</Bouton>
          <Bouton variante="primaire" disabled={enCours} onClick={() => void enregistrer()}>
            Enregistrer le retrait
          </Bouton>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* 2 · La RÉCIPROQUE, sur la fiche de l'équipement                           */
/* ------------------------------------------------------------------------ */

/**
 * « Qui peut travailler sur cet automate ? » — la même relation, lue de
 * l'autre côté. C'est la question qu'un auditeur pose en circulant, et elle
 * n'avait aucune réponse à l'écran avant ce lot.
 *
 * Rendue par POSTE et non par personne : les personnes s'en déduisent par le
 * planning et la matrice, qui existent déjà — les recalculer ici les ferait
 * diverger.
 */
export function PostesDeLEquipement({ documentId, version = 0 }: { documentId: string; version?: number }) {
  const roles = useGed((s) => s.session.roles);
  const [vue, setVue] = React.useState<VueEquipement | null>(null);
  const [erreur, setErreur] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [enCours, setEnCours] = React.useState(false);
  const [tour, setTour] = React.useState(0);
  // Retirer un ANCIEN lien posé : les mêmes rôles que le moteur (`saveEquipements`).
  const peutRetirer = ['rq', 'rt', 'rh'].some((r) => roles.includes(r));

  React.useEffect(() => {
    let vivant = true;
    void fetch(`/api/ged/ged/equip/${encodeURIComponent(documentId)}/postes`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!vivant) return; if (j) { setVue(j); setErreur(''); } else setErreur('Les postes de travail n’ont pas pu être lus.'); })
      .catch(() => { if (vivant) setErreur('Les postes de travail n’ont pas pu être lus.'); });
    return () => { vivant = false; };
  }, [documentId, tour, version]);

  if (erreur) return <p style={{ fontSize: 12.5, color: 'var(--amber-600)', margin: 0 }}>{erreur}</p>;
  if (!vue) return <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0 }}>Lecture…</p>;

  /* Retirer un lien posé AUTREFOIS à la main — le seul geste qui reste ici,
     le temps de la reprise. `base` ne nomme que cet appareil : le moteur ne
     retire que lui. */
  const retirer = async (poste: { code: string; libelle: string }) => {
    setEnCours(true); setMessage('');
    const r = await fetchEcriture(`/api/ged/ged/rh/postes/${encodeURIComponent(poste.code)}/equipements`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ equipements: [], base: [vue.code] }),
    });
    const d = await r.json().catch(() => null);
    setEnCours(false);
    if (!r.ok) { setMessage(d?.error?.message || 'Retrait refusé.'); return; }
    setMessage(`Ancien rattachement à « ${poste.libelle} » retiré.`);
    setTour((t) => t + 1);
  };

  const herites = vue.posteHerites || [];
  const deduits = vue.postes.filter((p) => p.origine === 'habilitation');
  const anciens = vue.postes.filter((p) => p.origine === 'pose');
  return (
    <div>
      {/* ⚠️ LE LIEN SE DÉDUIT, IL NE SE POSE PLUS (Ines, 28/10/2026) : « un
          équipement nécessite une habilitation et le poste nécessite une
          habilitation ». L'appareil sert à un poste dès que le poste exige une
          des habilitations qui le couvrent — on change l'un ou l'autre côté,
          jamais le lien lui-même. */}
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 8px', lineHeight: 1.5 }}>
        Les postes qui exigent une des habilitations de cet appareil — donc <strong>où il sert</strong>, qui
        doit être habilité dessus et qui reçoit ses maintenances au planning. Pour changer cette liste,
        modifiez les habilitations de l’appareil (bloc ci-dessus) ou celles qu’exige le poste.
      </p>
      {!!vue.note && <p style={{ fontSize: 12.5, color: 'var(--amber-600)', margin: '0 0 8px' }}>{vue.note}</p>}

      {!deduits.length && !anciens.length && !herites.length ? (
        <p style={{ fontSize: 12.5, margin: 0, lineHeight: 1.5 }}>
          <strong>Aucun poste.</strong> Aucun poste n’exige d’habilitation couvrant cet appareil : rattachez-le à
          l’habilitation de son activité pour qu’il apparaisse sur les postes qui l’exigent.
        </p>
      ) : (
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.7 }}>
          {deduits.map((p) => (
            <li key={`${p.code}-h`}>
              <strong>{p.libelle}</strong> <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>({p.code})</span>
              <span style={{ color: 'var(--text-muted)' }}> — exige {p.habilitation}</span>
            </li>
          ))}
          {anciens.map((p) => (
            <li key={`${p.code}-p`}>
              <strong>{p.libelle}</strong> <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>({p.code})</span>
              {' '}<span title="Posé à la main avant le 28/10/2026. Il sera converti en habilitation ou retiré à la reprise."
                style={{ fontSize: 12, fontWeight: 700, color: 'var(--amber-700)' }}>ancien rattachement à la main</span>
              {peutRetirer && !vue.sertAutomate && (
                <Bouton taille="sm" variante="discret" disabled={enCours} style={{ marginInlineStart: 6 }}
                  title={`Retirer l’ancien rattachement à « ${p.libelle} »`} onClick={() => void retirer(p)}>
                  <I n="x" s={12} /> Retirer
                </Bouton>
              )}
            </li>
          ))}
        </ul>
      )}
      {!!message && <p style={{ fontSize: 12.5, margin: '8px 0 0', fontWeight: 600 }}>{message}</p>}

      {!!vue.sertAutomate && (
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '10px 0 0', lineHeight: 1.5 }}>
          Cet équipement est un <strong>accessoire</strong> de {vue.sertAutomate} : il ne se rattache pas à un
          poste de son côté. {herites.length
            ? <>Il immobilise en revanche {herites.map((p) => p.libelle).join(', ')} — c’est ce qu’une panne
              de cet accessoire arrête réellement.</>
            : <>L’automate qu’il sert n’est lui-même rattaché à aucun poste.</>}
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* 2 bis · Les HABILITATIONS de l'appareil, choisies à l'ajout (28/10/2026)   */
/* ------------------------------------------------------------------------ */

const sansAccent = (v: string) => v.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

/**
 * Ce qu'il faut savoir pour se servir de l'appareil, dit DÈS L'AJOUT.
 *
 * « Un équipement nécessite une habilitation et le poste nécessite une
 * habilitation » (Ines, 28/10/2026) : on ne choisit plus de postes, on choisit
 * les habilitations — les postes qui les exigent recevront l'appareil d'eux-mêmes.
 *
 * Proposition PRÉ-COCHÉE et refusable, comme celle des postes avant elle
 * (`useHabilitationsProposees`) :
 *  · l'habilitation dont un appareil couvert a la MÊME discipline ;
 *  · ACT-SOCLE-TECHNIQUE pour un instrument de MÉTROLOGIE (décision du 28/10).
 * Dès qu'on touche une case, le formulaire garde SA sélection.
 */
export type HabilitationChoisissable = { code: string; libelle: string; actif: boolean; equipementCodes: string[] };

/** Les habilitations du référentiel, et celles qu'on PROPOSE pour un appareil de
 *  cette discipline / nature. Un hook, pour que le formulaire tienne lui-même
 *  la sélection : « proposée tant qu'on n'y a pas touché », sans remonter
 *  d'état depuis un rendu ni depuis un effet. */
export function useHabilitationsProposees(discipline?: string, nature?: string) {
  const equips = useGed((s) => s.equips);
  const [habs, setHabs] = React.useState<HabilitationChoisissable[] | null>(null);
  React.useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/rh/habilitations', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant) setHabs(((j?.habilitations || []) as (HabilitationChoisissable & { equipementCodes?: string[] })[])
        .filter((h) => h.actif !== false).map((h) => ({ ...h, equipementCodes: h.equipementCodes || [] }))); })
      .catch(() => { if (vivant) setHabs([]); });
    return () => { vivant = false; };
  }, []);
  const disciplineDe = React.useMemo(() => new Map(equips.filter((e) => e.code).map((e) => [String(e.code), sansAccent(String(e.discipline || ''))])), [equips]);
  const proposees = React.useMemo(() => {
    if (!habs || nature === 'accessoire') return [] as string[];
    if (nature === 'metrologie') return habs.some((h) => h.code === 'ACT-SOCLE-TECHNIQUE') ? ['ACT-SOCLE-TECHNIQUE'] : [];
    const d = sansAccent(discipline || '');
    if (!d) return [];
    return habs.filter((h) => h.equipementCodes.some((c) => disciplineDe.get(c) === d)).map((h) => h.code);
  }, [habs, discipline, nature, disciplineDe]);
  return { habs, proposees };
}

export function ChoixHabilitations({ habs, proposees, nature, choix, onChoix }: {
  habs: HabilitationChoisissable[] | null; proposees: string[]; nature?: string;
  choix: string[]; onChoix: (v: string[]) => void;
}) {
  if (!habs) return null;
  if (nature === 'accessoire') {
    return (
      <div style={encadre('var(--amber-50)', 'var(--amber-300)')}>
        <p style={{ fontSize: 12.5, margin: 0, lineHeight: 1.55 }}>
          Un <strong>accessoire</strong> n’exige pas d’habilitation : il suit l’automate qu’il sert, et les postes de cet automate.
        </p>
      </div>
    );
  }
  const basculer = (code: string) => onChoix(choix.includes(code) ? choix.filter((c) => c !== code) : [...choix, code]);
  const tri = [...habs].sort((a, b) => Number(proposees.includes(b.code)) - Number(proposees.includes(a.code)) || a.libelle.localeCompare(b.libelle, 'fr'));
  return (
    <div style={encadre('var(--gray-25)', 'var(--border-default)')}>
      <strong style={{ fontSize: 12.5 }}>Habilitations qu’exige cet appareil</strong>
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '4px 0 10px', lineHeight: 1.5 }}>
        L’appareil servira à chaque poste qui exige l’une d’elles. Proposées d’après la discipline saisie
        {nature === 'metrologie' ? ' (instrument de métrologie : l’habilitation socle commune)' : ''} — tout se décoche.
        Réservé au RQ : sans ce rôle, la fiche est créée et les habilitations restent à poser.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 5 }}>
        {tri.map((h) => (
          <label key={h.code} style={{ display: 'flex', gap: 7, alignItems: 'center', fontSize: 12.5, cursor: 'pointer' }}>
            <input type="checkbox" checked={choix.includes(h.code)} onChange={() => basculer(h.code)} />
            <span>{h.libelle}{proposees.includes(h.code) && <span style={{ color: 'var(--text-muted)' }}> · proposée</span>}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* 3 · Ce qui RESTE à faire dans le parc (28/10/2026)                         */
/* ------------------------------------------------------------------------ */

type EtatParc = {
  sansHabilitation: { code: string; nom: string; nature: string | null; discipline: string | null; statut: string | null }[];
  sansPosteExigeant: { code: string; libelle: string; equipements: string[] }[];
  chiffrage: Chiffrage;
};

/**
 * Le reste-à-faire du modèle « l'appareil exige une habilitation, le poste
 * exige une habilitation » (Ines, 28/10/2026). Il remplace l'état chiffré des
 * rattachements et la séance de reprise, qui servaient à POSER des postes à la
 * main — geste qui n'existe plus.
 *
 *  · un appareil SANS habilitation ne sert à aucun poste, et personne n'est
 *    tenu d'être habilité dessus : c'est le vrai trou ;
 *  · une habilitation qui couvre des appareils mais qu'AUCUN poste n'exige les
 *    laisse, eux aussi, hors de tout poste ;
 *  · les anciens rattachements à la main restent comptés jusqu'à la reprise.
 */
export function EtatHabilitationsParc() {
  const { select, go } = useGed();
  const [etat, setEtat] = React.useState<EtatParc | null>(null);
  const [erreur, setErreur] = React.useState('');

  React.useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/rh/postes/equipements', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!vivant) return; if (j) setEtat({ sansHabilitation: j.sansHabilitation || [], sansPosteExigeant: j.sansPosteExigeant || [], chiffrage: j.chiffrage }); else setErreur('L’état du parc n’a pas pu être lu.'); })
      .catch(() => { if (vivant) setErreur('L’état du parc n’a pas pu être lu.'); });
    return () => { vivant = false; };
  }, []);

  if (erreur) return <p style={{ fontSize: 12.5, color: 'var(--amber-600)', margin: 0 }}>{erreur}</p>;
  if (!etat) return null;
  const ouvrir = (code: string) => {
    const e = useGed.getState().equips.find((x) => x.code === code);
    if (e) { select('selectedEquip', e.documentId); go('equip'); }
  };

  return (
    <div style={{ ...card, padding: 18, marginTop: 14 }}>
      <h2 style={{ fontSize: 14, margin: '0 0 4px' }}>Appareils et habilitations — ce qui reste à faire</h2>
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 12px', lineHeight: 1.5 }}>
        Un appareil sert aux postes qui exigent une de ses habilitations. Sans habilitation, il ne sert à
        aucun poste et personne n’est tenu d’être habilité dessus. Recalculé à chaque lecture.
        {etat.chiffrage?.rattachesPose > 0 && <> Il reste <strong>{etat.chiffrage.rattachesPose}</strong> ancien(s) rattachement(s) à la main à reprendre.</>}
      </p>
      <Tableau
        id="parc-sans-habilitation"
        ajusterLargeur
        niveau="simple"
        recherche="Code, désignation, discipline…"
        lignes={etat.sansHabilitation}
        cleLigne={(e) => e.code}
        vide={{ titre: 'Tous les appareils ont une habilitation.', indice: 'Chacun sert aux postes qui l’exigent.' }}
        colonnes={[
          { cle: 'code', libelle: 'Code', valeur: (e) => e.code, style: { width: 80, fontFamily: 'var(--font-mono)' }, rendu: (e) => e.code },
          { cle: 'nom', libelle: 'Appareil sans habilitation', valeur: (e) => e.nom, rendu: (e) => e.nom },
          { cle: 'discipline', libelle: 'Discipline', valeur: (e) => e.discipline || '', rendu: (e) => e.discipline || '—' },
          { cle: 'nature', libelle: 'Nature', type: 'liste', valeur: (e) => e.nature || 'mesure', rendu: (e) => (e.nature === 'metrologie' ? 'métrologie' : e.nature || 'mesure') },
        ]}
        actions={(e) => (
          <Bouton taille="sm" variante="discret" onClick={() => ouvrir(e.code)} title="Ouvrir la fiche pour lui donner une habilitation">Ouvrir →</Bouton>
        )}
      />
      {!!etat.sansPosteExigeant.length && (
        <p style={{ fontSize: 12.5, color: 'var(--amber-700)', margin: '10px 0 0', lineHeight: 1.5 }}>
          Habilitation(s) qu’aucun poste n’exige — leurs appareils ne servent donc à aucun poste :{' '}
          {etat.sansPosteExigeant.map((h) => `${h.libelle} (${h.equipements.join(', ')})`).join(' · ')}.
        </p>
      )}
    </div>
  );
}
